import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import {
  getFeedbackActiveBar,
  getFeedbackBarCursor,
  getFeedbackLineCursor,
  getFeedbackStatCardClassName,
  getFeedbackTooltipStyle,
} from "./feedbackOverviewTheme";
import FeedbackDetailModal from "./FeedbackDetailModal";
import { buildPlayerProfileLookup, PlayerIdentityCell } from "./feedbackOverviewPlayer";
import FeedbackInsufficientDataPanel from "./FeedbackInsufficientDataPanel";
import SubjectiveOverviewExportMenu from "./SubjectiveOverviewExportMenu";
import {
  MIN_OVERVIEW_RESPONSES,
  countQuestionRespondents,
  countSubjectiveRespondents,
  canShowOverview,
  getMinResponsesForQuestionType,
  getOverviewMinRequired,
  getReadyDatesForQuestionHistory,
  getReadyDatesFromSubjectiveHistory,
} from "./feedbackOverviewThresholds";

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function average(values) {
  if (!Array.isArray(values) || values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function formatMetric(value, decimals = 1) {
  if (!Number.isFinite(value)) return "-";
  return Number(value).toFixed(decimals);
}

function formatDayLabel(isoDate) {
  if (!isoDate) return "";
  const date = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(isoDate);
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

function buildResponseMap(responses = []) {
  const map = new Map();
  (responses || []).forEach((response) => {
    const playerId = String(response?.player_id || "");
    if (!playerId) return;

    const previous = map.get(playerId);
    if (!previous) {
      map.set(playerId, response);
      return;
    }

    const currentDate = new Date(response?.submitted_at || 0).getTime();
    const previousDate = new Date(previous?.submitted_at || 0).getTime();

    if (currentDate >= previousDate) {
      map.set(playerId, response);
    }
  });
  return map;
}

function getAnswer(response, questionId) {
  const answers = Array.isArray(response?.answers) ? response.answers : [];
  const answer = answers.find((entry) => String(entry.question_id) === String(questionId));
  return answer?.answer;
}

function hasAnswer(value) {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

function parseYesNo(value) {
  if (value === true || value === false) return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "yes", "oui", "1"].includes(normalized)) return true;
    if (["false", "no", "non", "0"].includes(normalized)) return false;
  }
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  return null;
}

function toChoiceValues(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        const optionValue = getOptionValue(entry);
        return optionValue === null || optionValue === undefined ? "" : String(optionValue).trim();
      })
      .filter(Boolean);
  }

  if (value === null || value === undefined) return [];

  const optionValue = getOptionValue(value);
  const normalized = optionValue === null || optionValue === undefined ? "" : String(optionValue).trim();
  return normalized ? [normalized] : [];
}

function getOptionValue(option) {
  if (!option || typeof option !== "object") return option;
  return option.value ?? option.id ?? option.key ?? option.label;
}

function getOptionLabel(option) {
  if (!option || typeof option !== "object") return option;
  return option.label ?? option.name ?? option.title ?? getOptionValue(option);
}

function normalizeChoiceAlias(value) {
  return String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr-FR");
}

function normalizeChoiceOptions(options = []) {
  return (options || [])
    .map((option) => {
      const value = getOptionValue(option);
      if (value === null || value === undefined || value === "") return null;
      return {
        value: String(value),
        label: String(getOptionLabel(option) ?? value),
      };
    })
    .filter(Boolean);
}

function toTextValue(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

const TEXT_STOP_WORDS = new Set([
  "a",
  "ai",
  "au",
  "aux",
  "avec",
  "ce",
  "ces",
  "ca",
  "dans",
  "de",
  "des",
  "du",
  "elle",
  "en",
  "est",
  "et",
  "etre",
  "eu",
  "fait",
  "il",
  "je",
  "la",
  "le",
  "les",
  "leur",
  "lui",
  "ma",
  "mais",
  "me",
  "mes",
  "moi",
  "mon",
  "ne",
  "non",
  "nous",
  "on",
  "ont",
  "ou",
  "oui",
  "par",
  "pas",
  "peu",
  "plus",
  "pour",
  "que",
  "qui",
  "se",
  "ses",
  "son",
  "sur",
  "ta",
  "te",
  "tes",
  "toi",
  "tres",
  "tu",
  "un",
  "une",
  "vos",
  "vous",
  "the",
  "and",
  "for",
  "with",
  "this",
  "that",
  "have",
  "has",
  "was",
  "were",
  "not",
]);

function normalizeWordToken(raw) {
  return String(raw || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’]/g, "'")
    .toLowerCase()
    .replace(/^[a-z]{1,2}'/, "")
    .replace(/[^a-z0-9]/g, "");
}

function formatWordLabel(raw) {
  return String(raw || "")
    .replace(/[’]/g, "'")
    .replace(/^[\p{L}]{1,2}'/u, "")
    .replace(/_/g, "-")
    .toLocaleLowerCase("fr-FR");
}

function buildWordCloud(textEntries, limit = 32) {
  const counts = new Map();
  const labels = new Map();

  (textEntries || []).forEach((entry) => {
    const tokens = String(entry?.text || "").match(/[\p{L}\p{N}'’_-]+/gu) || [];
    tokens.forEach((token) => {
      const normalized = normalizeWordToken(token);
      if (normalized.length < 3 || TEXT_STOP_WORDS.has(normalized)) return;

      counts.set(normalized, (counts.get(normalized) || 0) + 1);
      if (!labels.has(normalized)) {
        labels.set(normalized, formatWordLabel(token));
      }
    });
  });

  return [...counts.entries()]
    .map(([key, count]) => ({
      key,
      label: labels.get(key) || key,
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, limit);
}

function formatSubmittedTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function toHour(value) {
  if (!value) return null;

  if (typeof value === "string") {
    const hourMatch = value.match(/^(\d{1,2}):(\d{2})/);
    if (hourMatch) {
      const hour = Number(hourMatch[1]);
      if (Number.isFinite(hour) && hour >= 0 && hour <= 23) return hour;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.getHours();
}

function periodFromHour(hour) {
  if (!Number.isFinite(hour)) return null;
  if (hour < 6) return "night";
  if (hour < 12) return "morning";
  if (hour < 18) return "afternoon";
  return "evening";
}

function flattenBodyMap(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => ({
      part: entry.part || "Unknown",
      side: entry.side || "",
      type: entry.type || "",
      severity: toNumber(entry.severity),
    }));
}

function normalizeBodyMapType(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "soreness" || normalized === "sore") return "soreness";
  if (normalized === "fatigue" || normalized === "tiredness") return "fatigue";
  if (normalized === "pain") return "pain";
  return "other";
}

function averageSeverityByType(entries, typeKey) {
  const values = (entries || [])
    .filter((entry) => normalizeBodyMapType(entry.type) === typeKey)
    .map((entry) => entry.severity)
    .filter((value) => Number.isFinite(value));
  return values.length ? average(values) : null;
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return "-";
  return `${formatMetric(value, 0)}%`;
}

const StatCard = ({ isDarkMode, label, value, hint, onClick }) => {
  const clickable = typeof onClick === "function";

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!clickable) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      className={`rounded-xl border p-3 transition-colors duration-150 ${getFeedbackStatCardClassName(isDarkMode)} ${
        clickable ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400/60" : ""
      }`}
    >
      <p className={`text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      {hint ? <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{hint}</p> : null}
    </div>
  );
};

const QuestionCard = ({ isDarkMode, title, badge, answered, total, children }) => (
  <div
    className={`rounded-2xl border p-4 ${
      isDarkMode ? "border-gray-700 bg-gray-800/80" : "border-gray-200 bg-white"
    }`}
  >
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div>
        <h4 className="text-base font-semibold">{title}</h4>
        <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          {answered}/{total}
        </p>
      </div>
      <span
        className={`rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${
          isDarkMode ? "bg-cyan-500/15 text-cyan-200" : "bg-cyan-50 text-cyan-700"
        }`}
      >
        {badge}
      </span>
    </div>
    {children}
  </div>
);

const EmptyState = ({ isDarkMode, text }) => (
  <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{text}</p>
);

function getWordSizeClass(count, maxCount) {
  if (!maxCount) return "text-sm";
  const ratio = count / maxCount;
  if (ratio >= 0.85) return "text-3xl";
  if (ratio >= 0.65) return "text-2xl";
  if (ratio >= 0.45) return "text-xl";
  if (ratio >= 0.25) return "text-lg";
  return "text-sm";
}

const TextQuestionOverviewCard = ({ insight, isDarkMode, t, onOpenDetail }) => {
  const [selectedPlayerId, setSelectedPlayerId] = React.useState(
    insight.responses[0]?.playerId || null
  );

  React.useEffect(() => {
    if (insight.responses.some((entry) => entry.playerId === selectedPlayerId)) return;
    setSelectedPlayerId(insight.responses[0]?.playerId || null);
  }, [insight.responses, selectedPlayerId]);

  const selectedResponse =
    insight.responses.find((entry) => entry.playerId === selectedPlayerId) ||
    insight.responses[0] ||
    null;
  const maxWordCount = insight.wordCloud[0]?.count || 0;

  return (
    <QuestionCard
      key={insight.id}
      isDarkMode={isDarkMode}
      title={insight.title}
      badge="text"
      answered={insight.answered}
      total={insight.total}
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.answers", { defaultValue: "Reponses" })}
          value={String(insight.count)}
          onClick={() =>
            onOpenDetail?.({
              title: insight.title,
              subtitle: t("feedback.answers", { defaultValue: "Reponses" }),
              rows: insight.responses,
            })
          }
        />
        <StatCard
          isDarkMode={isDarkMode}
          label={t("common.vs_prev", { defaultValue: "Vs veille" })}
          value={
            Number.isFinite(insight.previousCount)
              ? `${insight.count - insight.previousCount > 0 ? "+" : ""}${insight.count - insight.previousCount}`
              : "-"
          }
          onClick={() =>
            onOpenDetail?.({
              title: insight.title,
              subtitle: t("common.vs_prev", { defaultValue: "Vs veille" }),
              rows: insight.responses,
            })
          }
        />
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.words", { defaultValue: "Mots retenus" })}
          value={String(insight.wordCloud.length)}
          onClick={() =>
            onOpenDetail?.({
              title: insight.title,
              subtitle: t("feedback.words", { defaultValue: "Mots retenus" }),
              rows: insight.responses,
            })
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div
          className={`rounded-xl border p-3 ${
            isDarkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className={`text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("feedback.word_cloud", { defaultValue: "Nuage de mots" })}
            </p>
            <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("feedback.top_words", { defaultValue: "Mots frequents" })}
            </span>
          </div>

          {insight.wordCloud.length === 0 ? (
            <EmptyState
              isDarkMode={isDarkMode}
              text={t("feedback.no_word_cloud", { defaultValue: "Pas assez de mots significatifs." })}
            />
          ) : (
            <div className="flex min-h-48 flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 py-4 text-center">
              {insight.wordCloud.map((word) => (
                <span
                  key={word.key}
                  className={`${getWordSizeClass(word.count, maxWordCount)} font-semibold leading-tight ${
                    isDarkMode ? "text-cyan-200" : "text-cyan-700"
                  }`}
                  style={{ opacity: 0.62 + (word.count / maxWordCount) * 0.38 }}
                  title={`${word.count}`}
                >
                  {word.label}
                </span>
              ))}
            </div>
          )}
        </div>

        <div
          className={`rounded-xl border p-3 ${
            isDarkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-gray-50"
          }`}
        >
          <p className={`mb-3 text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {t("feedback.player_answers", { defaultValue: "Reponses joueurs" })}
          </p>

          {insight.responses.length === 0 ? (
            <EmptyState
              isDarkMode={isDarkMode}
              text={t("feedback.no_data", { defaultValue: "Aucune donnee disponible" })}
            />
          ) : (
            <>
              <div className="mb-3 flex max-h-28 flex-wrap gap-2 overflow-y-auto pr-1">
                {insight.responses.map((entry) => {
                  const selected = entry.playerId === selectedResponse?.playerId;
                  return (
                    <button
                      key={`${insight.id}-${entry.playerId}`}
                      type="button"
                      onClick={() => setSelectedPlayerId(entry.playerId)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                        selected
                          ? isDarkMode
                            ? "bg-cyan-400 text-gray-950"
                            : "bg-cyan-600 text-white"
                          : isDarkMode
                          ? "bg-gray-800 text-gray-200 hover:bg-gray-700/70"
                          : "bg-gray-100/80 text-gray-700 hover:bg-gray-200/70"
                      }`}
                    >
                      {entry.playerName}
                    </button>
                  );
                })}
              </div>

              {selectedResponse && (
                <div
                  className={`rounded-lg p-3 text-sm ${
                    isDarkMode ? "bg-gray-950/60 text-gray-100" : "bg-gray-100/60 text-gray-800"
                  }`}
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold">{selectedResponse.playerName}</p>
                    {formatSubmittedTime(selectedResponse.submittedAt) && (
                      <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                        {formatSubmittedTime(selectedResponse.submittedAt)}
                      </span>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap leading-relaxed">{selectedResponse.text}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </QuestionCard>
  );
};

const SubjectiveFeedbackOverview = ({
  questions = [],
  responses = [],
  previousResponses = [],
  history = [],
  displayedPlayers = [],
  templateName,
  sessionLabel,
  selectedDate = null,
  onSelectDate,
  isDarkMode = false,
  historyLoading = false,
  onRequestEdit,
  canEditInputs = true,
  onRequestReminder,
  isReminderLoading = false,
  reminderMessage = null,
  reminderError = null,
}) => {
  const { t } = useTranslation();
  const [detailModal, setDetailModal] = useState(null);

  const players = useMemo(
    () =>
      (displayedPlayers || []).map((player) => ({
        id: String(player.id),
        name: `${player.first_name || ""} ${player.last_name || ""}`.trim() || String(player.id),
        profilePicture: player.profile_picture || null,
      })),
    [displayedPlayers]
  );

  const playerProfileById = useMemo(
    () => buildPlayerProfileLookup(displayedPlayers),
    [displayedPlayers]
  );

  const allowedPlayerIds = useMemo(
    () => new Set(players.map((player) => player.id)),
    [players]
  );

  const currentResponseMap = useMemo(() => buildResponseMap(responses), [responses]);
  const previousResponseMap = useMemo(() => buildResponseMap(previousResponses), [previousResponses]);

  const historyMaps = useMemo(
    () =>
      (history || []).map((entry) => ({
        date: entry.date,
        label: formatDayLabel(entry.date),
        responseMap: buildResponseMap(entry.responses || []),
      })),
    [history]
  );

  const answeredPlayerCount = useMemo(() => {
    return players.filter((player) => {
      const response = currentResponseMap.get(player.id);
      const answers = Array.isArray(response?.answers) ? response.answers : [];
      return answers.some((answer) => hasAnswer(answer?.answer));
    }).length;
  }, [players, currentResponseMap]);

  const globalAnsweredPlayerCount = useMemo(
    () => countSubjectiveRespondents(responses),
    [responses]
  );

  const completionRate = useMemo(() => {
    if (!players.length) return 0;
    return (answeredPlayerCount / players.length) * 100;
  }, [answeredPlayerCount, players.length]);

  const scopedPlayerCount = players.length;
  const effectiveMinRequired = getOverviewMinRequired({
    minRequired: MIN_OVERVIEW_RESPONSES,
    scopedPlayerCount,
  });

  const hasOverviewData = canShowOverview({
    scopedAnswerCount: answeredPlayerCount,
    globalAnswerCount: globalAnsweredPlayerCount,
    minRequired: MIN_OVERVIEW_RESPONSES,
    scopedPlayerCount,
  });

  const previousReadyDates = useMemo(
    () =>
      getReadyDatesFromSubjectiveHistory(history, allowedPlayerIds, MIN_OVERVIEW_RESPONSES, selectedDate),
    [history, allowedPlayerIds, selectedDate]
  );

  const answeredQuestionsCount = useMemo(() => {
    if (!questions.length) return 0;

    return questions.reduce((count, question) => {
      const hasAny = players.some((player) => {
        const response = currentResponseMap.get(player.id);
        const answer = getAnswer(response, question.id);
        return hasAnswer(answer);
      });
      return hasAny ? count + 1 : count;
    }, 0);
  }, [questions, players, currentResponseMap]);

  const hasAnyAnswers = answeredQuestionsCount > 0;

  const renderQuestionInsufficient = (insight) => {
    const minRequired = getMinResponsesForQuestionType(insight.type);
    const effectiveQuestionMin = getOverviewMinRequired({
      minRequired,
      scopedPlayerCount,
    });
    const globalQuestionCount = countQuestionRespondents(responses, insight.id);
    const canShowQuestion = canShowOverview({
      scopedAnswerCount: insight.answered,
      globalAnswerCount: globalQuestionCount,
      minRequired,
      scopedPlayerCount,
    });
    if (canShowQuestion) return null;

    const questionPreviousDates = getReadyDatesForQuestionHistory(
      history,
      insight.id,
      allowedPlayerIds,
      minRequired,
      selectedDate
    );

    return (
      <QuestionCard
        key={insight.id}
        isDarkMode={isDarkMode}
        title={insight.title}
        badge={insight.type}
        answered={insight.answered}
        total={insight.total}
      >
        <FeedbackInsufficientDataPanel
          layout="compact"
          variant="sky"
          isDarkMode={isDarkMode}
          answerCount={insight.answered}
          minRequired={effectiveQuestionMin}
          previousDates={questionPreviousDates}
          onSelectDate={onSelectDate}
          title={t("feedback.question_insufficient_title", {
            defaultValue: "Not enough answers for this question",
          })}
          message={
            insight.answered === 0
              ? t("feedback.question_insufficient_empty", {
                  defaultValue: "No player has answered this question yet.",
                })
              : insight.type === "bodymap"
              ? t("feedback.bodymap_insufficient_message", {
                  defaultValue: "At least one player response is required to display the body map.",
                })
              : t("feedback.question_insufficient_message", {
                  defaultValue: "Not enough responses to display a reliable overview for this question.",
                })
          }
        />
      </QuestionCard>
    );
  };

  const answeredPlayerRows = useMemo(() => {
    return players
      .map((player) => {
        const response = currentResponseMap.get(player.id);
        const answers = questions
          .map((question) => ({
            question,
            value: getAnswer(response, question.id),
          }))
          .filter((entry) => hasAnswer(entry.value));

        if (!answers.length) return null;

        return {
          playerId: player.id,
          playerName: player.name,
          profilePicture: player.profilePicture,
          valueLabel: `${answers.length}/${questions.length}`,
          detail: answers
            .map((entry) => entry.question.label || entry.question.tag?.code || entry.question.type)
            .filter(Boolean)
            .join(", "),
          submittedAt: response?.submitted_at,
          sortValue: answers.length,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.sortValue - a.sortValue || a.playerName.localeCompare(b.playerName));
  }, [players, questions, currentResponseMap]);

  const insights = useMemo(() => {
    return questions.map((question, index) => {
      const questionId = String(question.id);
      const questionType = question.type || "text";
      const questionTitle =
        question.label ||
        question.tag?.code ||
        t("feedback.default_question", { defaultValue: `Question ${index + 1}` });

      const currentItems = [];
      const previousItems = [];

      players.forEach((player) => {
        const response = currentResponseMap.get(player.id);
        const answer = getAnswer(response, questionId);
        if (hasAnswer(answer)) {
          currentItems.push({
            playerId: player.id,
            playerName: player.name,
            value: answer,
            submittedAt: response?.submitted_at,
          });
        }

        const previousResponse = previousResponseMap.get(player.id);
        const previousAnswer = getAnswer(previousResponse, questionId);
        if (hasAnswer(previousAnswer)) {
          previousItems.push({
            playerId: player.id,
            playerName: player.name,
            value: previousAnswer,
            submittedAt: previousResponse?.submitted_at,
          });
        }
      });

      const base = {
        id: questionId,
        type: questionType,
        title: questionTitle,
        answered: currentItems.length,
        total: players.length,
        items: currentItems,
      };

      if (questionType === "scale") {
        const currentValues = currentItems
          .map((item) => toNumber(item.value))
          .filter((value) => Number.isFinite(value));
        const previousValues = previousItems
          .map((item) => toNumber(item.value))
          .filter((value) => Number.isFinite(value));

        const minCfg = toNumber(question?.config?.min);
        const maxCfg = toNumber(question?.config?.max);
        const min = Number.isFinite(minCfg) ? Math.floor(minCfg) : 0;
        const max = Number.isFinite(maxCfg) ? Math.ceil(maxCfg) : 10;

        const distribution = Array.from({ length: max - min + 1 }, (_, idx) => ({
          label: String(min + idx),
          count: 0,
          rows: [],
        }));

        currentItems.forEach((item) => {
          const value = toNumber(item.value);
          if (!Number.isFinite(value)) return;
          const bucket = Math.max(min, Math.min(max, Math.round(value)));
          distribution[bucket - min].count += 1;
          distribution[bucket - min].rows.push({
            ...item,
            valueLabel: formatMetric(value),
            sortValue: value,
          });
        });

        const trend = historyMaps.map((day) => {
          const values = players
            .map((player) => {
              const response = day.responseMap.get(player.id);
              return toNumber(getAnswer(response, questionId));
            })
            .filter((value) => Number.isFinite(value));

          return {
            label: day.label,
            average: values.length ? average(values) : null,
          };
        });

        return {
          ...base,
          currentAverage: average(currentValues),
          previousAverage: average(previousValues),
          min: currentValues.length ? Math.min(...currentValues) : null,
          max: currentValues.length ? Math.max(...currentValues) : null,
          detailRows: currentItems
            .map((item) => {
              const value = toNumber(item.value);
              if (!Number.isFinite(value)) return null;
              return {
                ...item,
                valueLabel: formatMetric(value),
                sortValue: value,
              };
            })
            .filter(Boolean),
          distribution,
          trend,
        };
      }

      if (questionType === "yesno") {
        const currentAnswers = currentItems
          .map((item) => parseYesNo(item.value))
          .filter((value) => value !== null);
        const previousAnswers = previousItems
          .map((item) => parseYesNo(item.value))
          .filter((value) => value !== null);

        const yesCount = currentAnswers.filter(Boolean).length;
        const noCount = currentAnswers.filter((value) => !value).length;
        const yesRows = [];
        const noRows = [];
        currentItems.forEach((item) => {
          const parsed = parseYesNo(item.value);
          if (parsed === null) return;
          const row = {
            ...item,
            valueLabel: parsed
              ? t("common.yes", { defaultValue: "Yes" })
              : t("common.no", { defaultValue: "No" }),
            sortValue: parsed ? 1 : 0,
          };
          if (parsed) yesRows.push(row);
          else noRows.push(row);
        });

        const prevYesCount = previousAnswers.filter(Boolean).length;
        const prevRate = previousAnswers.length ? (prevYesCount / previousAnswers.length) * 100 : null;

        const trend = historyMaps.map((day) => {
          const answers = players
            .map((player) => {
              const response = day.responseMap.get(player.id);
              return parseYesNo(getAnswer(response, questionId));
            })
            .filter((value) => value !== null);

          const yes = answers.filter(Boolean).length;
          const rate = answers.length ? (yes / answers.length) * 100 : null;
          return {
            label: day.label,
            yesRate: rate,
          };
        });

        return {
          ...base,
          yesCount,
          noCount,
          yesRate: currentAnswers.length ? (yesCount / currentAnswers.length) * 100 : null,
          previousYesRate: prevRate,
          detailRows: [...yesRows, ...noRows],
          distribution: [
            { label: t("common.yes", { defaultValue: "Yes" }), count: yesCount, rows: yesRows },
            { label: t("common.no", { defaultValue: "No" }), count: noCount, rows: noRows },
          ],
          trend,
        };
      }

      if (questionType === "choice") {
        const options = normalizeChoiceOptions(question.options || question.config?.options || []);
        const optionLabels = new Map(options.map((option) => [option.value, option.label]));
        const optionAliases = new Map();

        options.forEach((option) => {
          [option.value, option.label].forEach((alias) => {
            const normalized = normalizeChoiceAlias(alias);
            if (normalized) optionAliases.set(normalized, option.value);
          });
        });

        const resolveChoiceKey = (choice) => {
          const rawValue = getOptionValue(choice);
          const rawLabel = getOptionLabel(choice);
          const raw = rawValue === null || rawValue === undefined ? rawLabel : rawValue;
          const normalized = normalizeChoiceAlias(raw);
          const optionKey = optionAliases.get(normalized);

          if (optionKey) return optionKey;

          const customKey = `custom:${normalized || String(raw || "").trim()}`;
          if (!optionLabels.has(customKey)) {
            optionLabels.set(customKey, String(rawLabel || raw || ""));
          }
          return customKey;
        };

        const collectChoiceCounts = (items) => {
          const counts = new Map();
          const rows = new Map();
          let respondentCount = 0;

          items.forEach((item) => {
            const selectedKeys = new Set(toChoiceValues(item.value).map(resolveChoiceKey).filter(Boolean));
            if (selectedKeys.size > 0) respondentCount += 1;
            selectedKeys.forEach((key) => {
              counts.set(key, (counts.get(key) || 0) + 1);
              const nextRows = rows.get(key) || [];
              nextRows.push({
                ...item,
                valueLabel: optionLabels.get(key) || key,
              });
              rows.set(key, nextRows);
            });
          });

          return { counts, respondentCount, rows };
        };

        const currentChoiceStats = collectChoiceCounts(currentItems);
        const previousChoiceStats = collectChoiceCounts(previousItems);
        const optionKeys = new Set([
          ...currentChoiceStats.counts.keys(),
          ...previousChoiceStats.counts.keys(),
        ]);

        const distribution = [...optionKeys]
          .map((key) => {
            const count = currentChoiceStats.counts.get(key) || 0;
            const previousCount = previousChoiceStats.counts.get(key) || 0;
            const percent = currentChoiceStats.respondentCount
              ? (count / currentChoiceStats.respondentCount) * 100
              : null;
            const previousPercent = previousChoiceStats.respondentCount
              ? (previousCount / previousChoiceStats.respondentCount) * 100
              : null;

            return {
              key,
              label: optionLabels.get(key) || key,
              count,
              previousCount,
              rows: currentChoiceStats.rows.get(key) || [],
              percent,
              previousPercent,
              delta:
                Number.isFinite(percent) && Number.isFinite(previousPercent)
                  ? percent - previousPercent
                  : null,
            };
          })
          .filter((entry) => entry.count > 0 || entry.previousCount > 0)
          .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
          .slice(0, 10);

        return {
          ...base,
          distribution,
          detailRows: currentItems
            .map((item) => ({
              ...item,
              valueLabel: toChoiceValues(item.value)
                .map((choice) => optionLabels.get(resolveChoiceKey(choice)) || String(choice))
                .join(", "),
            }))
            .filter((item) => item.valueLabel),
          responseRate: players.length ? (currentChoiceStats.respondentCount / players.length) * 100 : null,
          previousResponseRate: players.length ? (previousChoiceStats.respondentCount / players.length) * 100 : null,
        };
      }

      if (questionType === "time") {
        const periodOrder = ["night", "morning", "afternoon", "evening"];
        const periodMap = new Map(periodOrder.map((periodKey) => [periodKey, { count: 0, rows: [] }]));

        const currentHours = currentItems
          .map((item) => toHour(item.value))
          .filter((hour) => Number.isFinite(hour));

        currentItems.forEach((item) => {
          const hour = toHour(item.value);
          if (!Number.isFinite(hour)) return;
          const period = periodFromHour(hour);
          if (!period) return;
          const entry = periodMap.get(period) || { count: 0, rows: [] };
          entry.count += 1;
          entry.rows.push({
            ...item,
            valueLabel: `${hour}h`,
            sortValue: hour,
          });
          periodMap.set(period, entry);
        });

        const trend = historyMaps.map((day) => {
          const hours = players
            .map((player) => {
              const response = day.responseMap.get(player.id);
              return toHour(getAnswer(response, questionId));
            })
            .filter((hour) => Number.isFinite(hour));

          return {
            label: day.label,
            averageHour: hours.length ? average(hours) : null,
          };
        });

        return {
          ...base,
          averageHour: currentHours.length ? average(currentHours) : null,
          detailRows: currentItems
            .map((item) => {
              const hour = toHour(item.value);
              if (!Number.isFinite(hour)) return null;
              return {
                ...item,
                valueLabel: `${hour}h`,
                sortValue: hour,
              };
            })
            .filter(Boolean),
          distribution: periodOrder.map((periodKey) => ({
            label: t(`feedback.period_${periodKey}`, {
              defaultValue: periodKey,
            }),
            count: periodMap.get(periodKey)?.count || 0,
            rows: periodMap.get(periodKey)?.rows || [],
          })),
          trend,
        };
      }

      if (questionType === "bodymap") {
        const currentEntries = currentItems.flatMap((item) =>
          flattenBodyMap(item.value).map((entry) => ({
            ...entry,
            playerId: item.playerId,
            playerName: item.playerName,
            submittedAt: item.submittedAt,
          }))
        );

        const previousEntries = previousItems.flatMap((item) => flattenBodyMap(item.value));

        const zoneCounts = new Map();
        currentEntries.forEach((entry) => {
          const zone = `${entry.part}${entry.side ? ` (${entry.side})` : ""}`;
          const current = zoneCounts.get(zone) || { count: 0, rows: [] };
          current.count += 1;
          current.rows.push({
            ...entry,
            valueLabel: Number.isFinite(entry.severity) ? formatMetric(entry.severity) : "-",
            detail: [entry.type, entry.part, entry.side].filter(Boolean).join(" - "),
            sortValue: entry.severity,
          });
          zoneCounts.set(zone, current);
        });

        const topZones = [...zoneCounts.entries()]
          .map(([label, entry]) => ({ label, count: entry.count, rows: entry.rows }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 8);

        const bodymapTypeAverages = [
          {
            key: "pain",
            label: t("bodymap.pain", { defaultValue: "Pain" }),
            current: averageSeverityByType(currentEntries, "pain"),
            previous: averageSeverityByType(previousEntries, "pain"),
            rows: currentEntries
              .filter((entry) => normalizeBodyMapType(entry.type) === "pain")
              .map((entry) => ({
                ...entry,
                valueLabel: Number.isFinite(entry.severity) ? formatMetric(entry.severity) : "-",
                detail: [entry.part, entry.side].filter(Boolean).join(" - "),
                sortValue: entry.severity,
              })),
          },
          {
            key: "soreness",
            label: t("bodymap.soreness", { defaultValue: "Soreness" }),
            current: averageSeverityByType(currentEntries, "soreness"),
            previous: averageSeverityByType(previousEntries, "soreness"),
            rows: currentEntries
              .filter((entry) => normalizeBodyMapType(entry.type) === "soreness")
              .map((entry) => ({
                ...entry,
                valueLabel: Number.isFinite(entry.severity) ? formatMetric(entry.severity) : "-",
                detail: [entry.part, entry.side].filter(Boolean).join(" - "),
                sortValue: entry.severity,
              })),
          },
          {
            key: "fatigue",
            label: t("bodymap.fatigue", { defaultValue: "Fatigue" }),
            current: averageSeverityByType(currentEntries, "fatigue"),
            previous: averageSeverityByType(previousEntries, "fatigue"),
            rows: currentEntries
              .filter((entry) => normalizeBodyMapType(entry.type) === "fatigue")
              .map((entry) => ({
                ...entry,
                valueLabel: Number.isFinite(entry.severity) ? formatMetric(entry.severity) : "-",
                detail: [entry.part, entry.side].filter(Boolean).join(" - "),
                sortValue: entry.severity,
              })),
          },
        ];

        return {
          ...base,
          topZones,
          bodymapTypeAverages,
          detailRows: currentEntries.map((entry) => ({
            ...entry,
            valueLabel: Number.isFinite(entry.severity) ? formatMetric(entry.severity) : "-",
            detail: [entry.type, entry.part, entry.side].filter(Boolean).join(" - "),
            sortValue: entry.severity,
          })),
        };
      }

      const textEntries = currentItems
        .map((item) => ({
          ...item,
          text: toTextValue(item.value),
        }))
        .filter((item) => item.text);

      const previousTextCount = previousItems
        .map((item) => toTextValue(item.value))
        .filter(Boolean).length;

      const sortedResponses = [...textEntries].sort((a, b) =>
        a.playerName.localeCompare(b.playerName)
      );

      return {
        ...base,
        count: textEntries.length,
        previousCount: previousTextCount,
        responses: sortedResponses,
        detailRows: sortedResponses.map((entry) => ({
          ...entry,
          valueLabel: entry.text,
        })),
        wordCloud: buildWordCloud(textEntries),
      };
    });
  }, [
    questions,
    players,
    t,
    currentResponseMap,
    previousResponseMap,
    historyMaps,
  ]);

  const tooltipStyle = getFeedbackTooltipStyle(isDarkMode);
  const barCursor = getFeedbackBarCursor(isDarkMode);
  const lineCursor = getFeedbackLineCursor(isDarkMode);
  const activeBar = getFeedbackActiveBar(isDarkMode);

  const formatDetailRawValue = (value) => {
    if (value === true) return t("common.yes", { defaultValue: "Yes" });
    if (value === false) return t("common.no", { defaultValue: "No" });
    if (Number.isFinite(Number(value)) && value !== "" && value !== null) return formatMetric(Number(value));
    if (Array.isArray(value)) {
      return value.map((entry) => String(getOptionLabel(entry) ?? entry)).join(", ");
    }
    if (value && typeof value === "object") {
      return String(getOptionLabel(value) ?? JSON.stringify(value));
    }
    return value === null || value === undefined || value === "" ? "-" : String(value);
  };

  const normalizeDetailRows = (detailRows = []) =>
    [...detailRows]
      .map((row) => {
        const playerId = String(row.playerId || row.player_id || row.id || "").trim();
        return {
          id: playerId || row.id,
          playerName: row.playerName || row.player_name || row.name || "-",
          profilePicture: row.profilePicture || playerProfileById.get(playerId) || null,
          valueLabel: row.valueLabel || formatDetailRawValue(row.value),
          detail: row.detail || "",
          submittedAt: row.submittedAt || row.submitted_at,
          sortValue: Number.isFinite(row.sortValue) ? row.sortValue : null,
        };
      })
      .sort((a, b) => {
        if (Number.isFinite(a.sortValue) && Number.isFinite(b.sortValue) && a.sortValue !== b.sortValue) {
          return b.sortValue - a.sortValue;
        }
        return a.playerName.localeCompare(b.playerName);
      });

  const openDetail = ({ title, subtitle, rows }) => {
    setDetailModal({
      title,
      subtitle,
      rows: normalizeDetailRows(rows),
    });
  };

  const detailColumns = useMemo(
    () => [
      {
        key: "playerName",
        label: t("table.player", { defaultValue: "Joueur" }),
        render: (row) => (
          <PlayerIdentityCell
            name={row.playerName}
            profilePicture={row.profilePicture}
            isDarkMode={isDarkMode}
          />
        ),
      },
      {
        key: "valueLabel",
        label: t("feedback.answer", { defaultValue: "Réponse" }),
        className: "font-medium",
      },
      {
        key: "detail",
        label: t("common.details", { defaultValue: "Détails" }),
        render: (row) => row.detail || "-",
      },
      {
        key: "submittedAt",
        label: t("common.time", { defaultValue: "Heure" }),
        render: (row) => formatSubmittedTime(row.submittedAt) || "-",
      },
    ],
    [t, isDarkMode]
  );

  const formatExportCellValue = (value, questionType) => {
    if (!hasAnswer(value)) return "";
    if (questionType === "bodymap") {
      return flattenBodyMap(value)
        .map((entry) => {
          const zone = [entry.part, entry.side].filter(Boolean).join(" ");
          const severity = Number.isFinite(entry.severity) ? formatMetric(entry.severity) : "-";
          return `${zone} (${entry.type || "?"}: ${severity})`;
        })
        .join(" | ");
    }
    return formatDetailRawValue(value);
  };

  const subjectiveExportData = useMemo(() => {
    const playerAnswers = insights.flatMap((insight) =>
      (insight.detailRows || []).map((row) => ({
        playerName: row.playerName || "",
        questionTitle: insight.title,
        questionType: insight.type,
        answer: row.valueLabel ?? "",
        detail: row.detail ?? "",
        submittedAt: row.submittedAt ? formatSubmittedTime(row.submittedAt) : "",
      }))
    );

    const playersWide = players.map((player) => {
      const response = currentResponseMap.get(player.id);
      const row = { Player: player.name };
      questions.forEach((question, index) => {
        const title =
          question.label ||
          question.tag?.code ||
          t("feedback.default_question", { defaultValue: `Question ${index + 1}` });
        const answer = getAnswer(response, question.id);
        row[title] = formatExportCellValue(answer, question.type || "text");
      });
      return row;
    });

    return {
      selectedDate,
      templateName,
      sessionLabel,
      summary: {
        playerCount: players.length,
        answeredPlayers: answeredPlayerCount,
        completionRate,
        answeredQuestions: answeredQuestionsCount,
        totalQuestions: questions.length,
      },
      questions: insights.map((insight) => ({
        title: insight.title,
        type: insight.type,
        answered: insight.answered,
        total: insight.total,
        currentAverage: insight.currentAverage,
        previousAverage: insight.previousAverage,
        min: insight.min,
        max: insight.max,
        yesCount: insight.yesCount,
        noCount: insight.noCount,
        yesRate: insight.yesRate,
        previousYesRate: insight.previousYesRate,
        averageHour: insight.averageHour,
        count: insight.count,
        previousCount: insight.previousCount,
        bodymapTypeAverages: insight.bodymapTypeAverages,
        topZones: insight.topZones,
        distribution: insight.distribution,
        trend: insight.trend,
        responseRate: insight.responseRate,
        previousResponseRate: insight.previousResponseRate,
      })),
      playerAnswers,
      playersWide,
    };
  }, [
    insights,
    players,
    questions,
    currentResponseMap,
    selectedDate,
    templateName,
    sessionLabel,
    answeredPlayerCount,
    completionRate,
    answeredQuestionsCount,
    t,
  ]);

  return (
    <div className="space-y-4">
      <div
        className={`rounded-2xl border p-4 ${
          isDarkMode ? "border-sky-500/30 bg-sky-500/5" : "border-sky-200 bg-sky-50"
        }`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className={`text-xs uppercase tracking-wide ${isDarkMode ? "text-sky-300" : "text-sky-700"}`}>
              {t("feedback.hooper_title", { defaultValue: "Hooper Feedback" })}
            </p>
            <h3 className="mt-1 text-xl font-semibold">
              {t("feedback.overview_title", { defaultValue: "Overview des resultats" })}
            </h3>
            <p className={`mt-1 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              {t("feedback.hooper_overview_subtitle", {
                defaultValue:
                  "Comparaisons automatiques et graphiques adaptes selon le type de question.",
              })}
            </p>
            {(templateName || sessionLabel) && (
              <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {[templateName, sessionLabel].filter(Boolean).join(" - ")}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {typeof onRequestReminder === "function" && (
              <button
                type="button"
                onClick={onRequestReminder}
                disabled={isReminderLoading}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${
                  isDarkMode
                    ? "bg-amber-500/90 text-gray-950 hover:bg-amber-400"
                    : "bg-amber-600 text-white hover:bg-amber-700"
                }`}
              >
                {isReminderLoading
                  ? t("feedback.hooper_remind_sending", { defaultValue: "Sending…" })
                  : t("feedback.hooper_remind_button", { defaultValue: "Send reminder" })}
              </button>
            )}
            <SubjectiveOverviewExportMenu
              data={subjectiveExportData}
              isDarkMode={isDarkMode}
              disabled={!selectedDate}
            />
            {canEditInputs && typeof onRequestEdit === "function" && (
              <button
                type="button"
                onClick={onRequestEdit}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                  isDarkMode
                    ? "bg-sky-400 text-gray-900 hover:bg-sky-300"
                    : "bg-sky-600 text-white hover:bg-sky-700"
                }`}
              >
                {t("feedback.edit_entry", { defaultValue: "Modification de la saisie" })}
              </button>
            )}
          </div>
        </div>
        {(reminderMessage || reminderError) && (
          <p className={`mt-3 text-sm ${reminderError ? (isDarkMode ? "text-rose-300" : "text-rose-600") : (isDarkMode ? "text-emerald-300" : "text-emerald-700")}`}>
            {reminderError || reminderMessage}
          </p>
        )}
      </div>

      {!hasAnyAnswers ? (
        <FeedbackInsufficientDataPanel
          isDarkMode={isDarkMode}
          variant="sky"
          layout="hero"
          hideProgress
          answerCount={0}
          minRequired={effectiveMinRequired}
          previousDates={previousReadyDates}
          onSelectDate={onSelectDate}
          title={t("feedback.hooper_empty_title", {
            defaultValue: "Subjective questionnaire not answered",
          })}
          message={t("feedback.hooper_empty_message", {
            defaultValue: "No player has answered the morning or afternoon questionnaire yet.",
          })}
        />
      ) : (
        <>
          {!hasOverviewData ? (
            <FeedbackInsufficientDataPanel
              isDarkMode={isDarkMode}
              variant="sky"
              layout="hero"
              answerCount={answeredPlayerCount}
              minRequired={effectiveMinRequired}
              previousDates={previousReadyDates}
              onSelectDate={onSelectDate}
              title={t("feedback.hooper_insufficient_title", {
                defaultValue: "Hooper questionnaire not yet completed",
              })}
              message={t("feedback.hooper_insufficient_message", {
                defaultValue:
                  "Players have not yet answered the morning/afternoon questionnaire for this day.",
              })}
            />
          ) : (
            <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.kpi_players", { defaultValue: "Joueurs analyses" })}
          value={String(players.length)}
          onClick={() =>
            openDetail({
              title: t("feedback.kpi_players", { defaultValue: "Joueurs analyses" }),
              subtitle: t("feedback.kpi_answered_players", {
                defaultValue: "{{count}} joueurs ont répondu",
                count: answeredPlayerCount,
              }),
              rows: answeredPlayerRows,
            })
          }
          hint={t("feedback.kpi_questions", {
            defaultValue: "{{count}} questions",
            count: questions.length,
          })}
        />
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.kpi_completion", { defaultValue: "Taux de reponse" })}
          value={`${formatMetric(completionRate, 0)}%`}
          onClick={() =>
            openDetail({
              title: t("feedback.kpi_completion", { defaultValue: "Taux de reponse" }),
              subtitle: t("feedback.kpi_answered_players", {
                defaultValue: "{{count}} joueurs ont répondu",
                count: answeredPlayerCount,
              }),
              rows: answeredPlayerRows,
            })
          }
          hint={t("feedback.kpi_answered_players", {
            defaultValue: "{{count}} joueurs ont répondu",
            count: answeredPlayerCount,
          })}
        />
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.kpi_answered_questions", { defaultValue: "Questions remplies" })}
          value={`${answeredQuestionsCount}/${questions.length}`}
          onClick={() =>
            openDetail({
              title: t("feedback.kpi_answered_questions", { defaultValue: "Questions remplies" }),
              subtitle: t("feedback.kpi_questions", {
                defaultValue: "{{count}} questions",
                count: questions.length,
              }),
              rows: answeredPlayerRows,
            })
          }
          hint={historyLoading ? t("feedback.loading_history", { defaultValue: "Chargement de l'historique..." }) : null}
        />
        <StatCard
          isDarkMode={isDarkMode}
          label={t("feedback.kpi_mode", { defaultValue: "Mode" })}
          value={t("feedback.overview_mode", { defaultValue: "Overview" })}
          hint={
            canEditInputs
              ? t("feedback.kpi_mode_hint", {
                  defaultValue: "Passez en edition pour corriger une saisie manuellement.",
                })
              : null
          }
        />
            </div>
            </>
          )}

          <div className="space-y-4">
            {insights.map((insight) => {
          const insufficientView = renderQuestionInsufficient(insight);
          if (insufficientView) return insufficientView;

          if (insight.type === "scale") {
            const delta =
              Number.isFinite(insight.currentAverage) && Number.isFinite(insight.previousAverage)
                ? insight.currentAverage - insight.previousAverage
                : null;

            return (
              <QuestionCard
                key={insight.id}
                isDarkMode={isDarkMode}
                title={insight.title}
                badge="scale"
                answered={insight.answered}
                total={insight.total}
              >
                <div className="mb-3 grid gap-3 sm:grid-cols-3">
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("feedback.kpi_average", { defaultValue: "Moyenne" })}
                    value={formatMetric(insight.currentAverage)}
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("feedback.kpi_average", { defaultValue: "Moyenne" }),
                        rows: insight.detailRows,
                      })
                    }
                  />
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("common.vs_prev", { defaultValue: "Vs veille" })}
                    value={delta === null ? "-" : `${delta > 0 ? "+" : ""}${formatMetric(delta)}`}
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("common.vs_prev", { defaultValue: "Vs veille" }),
                        rows: insight.detailRows,
                      })
                    }
                  />
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("feedback.kpi_min_max", { defaultValue: "Min/Max" })}
                    value={
                      insight.min === null || insight.max === null
                        ? "-"
                        : `${formatMetric(insight.min)} / ${formatMetric(insight.max)}`
                    }
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("feedback.kpi_min_max", { defaultValue: "Min/Max" }),
                        rows: insight.detailRows,
                      })
                    }
                  />
                </div>

                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={insight.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                        <XAxis dataKey="label" tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <YAxis tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} cursor={lineCursor} formatter={(value) => formatMetric(value)} />
                        <Line dataKey="average" type="monotone" stroke="#06b6d4" strokeWidth={2.5} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={insight.distribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                        <XAxis dataKey="label" tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <YAxis allowDecimals={false} tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} cursor={barCursor} />
                        <Bar
                          dataKey="count"
                          fill="#0891b2"
                          radius={[6, 6, 0, 0]}
                          activeBar={activeBar}
                          className="cursor-pointer"
                          onClick={(entry) => {
                            const bucket = entry?.payload;
                            openDetail({
                              title: `${insight.title} - ${bucket?.label ?? ""}`,
                              subtitle: t("feedback.answers", { defaultValue: "Reponses" }),
                              rows: bucket?.rows || [],
                            });
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </QuestionCard>
            );
          }

          if (insight.type === "yesno") {
            const delta =
              Number.isFinite(insight.yesRate) && Number.isFinite(insight.previousYesRate)
                ? insight.yesRate - insight.previousYesRate
                : null;

            return (
              <QuestionCard
                key={insight.id}
                isDarkMode={isDarkMode}
                title={insight.title}
                badge="yes/no"
                answered={insight.answered}
                total={insight.total}
              >
                <div className="mb-3 grid gap-3 sm:grid-cols-3">
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("feedback.yes_rate", { defaultValue: "Taux de Oui" })}
                    value={formatMetric(insight.yesRate, 0) + "%"}
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("feedback.yes_rate", { defaultValue: "Taux de Oui" }),
                        rows: insight.distribution[0]?.rows || [],
                      })
                    }
                  />
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("common.vs_prev", { defaultValue: "Vs veille" })}
                    value={delta === null ? "-" : `${delta > 0 ? "+" : ""}${formatMetric(delta, 0)}%`}
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("common.vs_prev", { defaultValue: "Vs veille" }),
                        rows: insight.detailRows,
                      })
                    }
                  />
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("feedback.answers_split", { defaultValue: "Oui / Non" })}
                    value={`${insight.yesCount} / ${insight.noCount}`}
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("feedback.answers_split", { defaultValue: "Oui / Non" }),
                        rows: insight.detailRows,
                      })
                    }
                  />
                </div>

                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={insight.distribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                        <XAxis dataKey="label" tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <YAxis allowDecimals={false} tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} cursor={barCursor} />
                        <Bar
                          dataKey="count"
                          fill="#0284c7"
                          radius={[6, 6, 0, 0]}
                          activeBar={activeBar}
                          className="cursor-pointer"
                          onClick={(entry) => {
                            const bucket = entry?.payload;
                            openDetail({
                              title: `${insight.title} - ${bucket?.label ?? ""}`,
                              subtitle: t("feedback.answers", { defaultValue: "Reponses" }),
                              rows: bucket?.rows || [],
                            });
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={insight.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                        <XAxis dataKey="label" tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <YAxis domain={[0, 100]} tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} cursor={lineCursor} formatter={(value) => `${formatMetric(value, 0)}%`} />
                        <Line dataKey="yesRate" type="monotone" stroke="#0ea5e9" strokeWidth={2.5} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </QuestionCard>
            );
          }

          if (insight.type === "choice") {
            const responseRateDelta =
              Number.isFinite(insight.responseRate) && Number.isFinite(insight.previousResponseRate)
                ? insight.responseRate - insight.previousResponseRate
                : null;

            return (
              <QuestionCard
                key={insight.id}
                isDarkMode={isDarkMode}
                title={insight.title}
                badge="choice"
                answered={insight.answered}
                total={insight.total}
              >
                <div className="mb-3 grid gap-3 sm:grid-cols-3">
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("feedback.kpi_completion", { defaultValue: "Taux de reponse" })}
                    value={formatPercent(insight.responseRate)}
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("feedback.kpi_completion", { defaultValue: "Taux de reponse" }),
                        rows: insight.detailRows,
                      })
                    }
                  />
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("common.vs_prev", { defaultValue: "Vs veille" })}
                    value={
                      responseRateDelta === null
                        ? "-"
                        : `${responseRateDelta > 0 ? "+" : ""}${formatMetric(responseRateDelta, 0)}%`
                    }
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("common.vs_prev", { defaultValue: "Vs veille" }),
                        rows: insight.detailRows,
                      })
                    }
                  />
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("feedback.answers", { defaultValue: "Reponses" })}
                    value={`${insight.answered}/${insight.total}`}
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("feedback.answers", { defaultValue: "Reponses" }),
                        rows: insight.detailRows,
                      })
                    }
                  />
                </div>

                {insight.distribution.length === 0 || insight.answered === 0 ? (
                  <EmptyState
                    isDarkMode={isDarkMode}
                    text={t("feedback.no_data", { defaultValue: "Aucune donnee disponible" })}
                  />
                ) : (
                  <div
                    className={`rounded-xl border p-3 ${
                      isDarkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="space-y-3">
                      {insight.distribution.map((entry) => {
                        const percent = Number.isFinite(entry.percent) ? entry.percent : 0;
                        const previousPercent = Number.isFinite(entry.previousPercent) ? entry.previousPercent : null;
                        const deltaLabel = Number.isFinite(entry.delta)
                          ? `${entry.delta > 0 ? "+" : ""}${formatMetric(entry.delta, 0)}%`
                          : t("feedback.no_comparison", { defaultValue: "Pas de comparaison disponible" });

                        return (
                          <div
                            key={entry.key}
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              openDetail({
                                title: `${insight.title} - ${entry.label}`,
                                subtitle: t("feedback.answers", { defaultValue: "Reponses" }),
                                rows: entry.rows,
                              })
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                openDetail({
                                  title: `${insight.title} - ${entry.label}`,
                                  subtitle: t("feedback.answers", { defaultValue: "Reponses" }),
                                  rows: entry.rows,
                                });
                              }
                            }}
                            className={`grid cursor-pointer gap-2 rounded-lg p-2 transition-colors md:grid-cols-[minmax(10rem,16rem)_minmax(0,1fr)_5rem] md:items-center ${
                              isDarkMode ? "hover:bg-gray-800/80" : "hover:bg-slate-100/80"
                            }`}
                          >
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium" title={entry.label}>
                                {entry.label}
                              </div>
                              <div className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                                {entry.count} / {insight.answered}
                              </div>
                            </div>

                            <div className="min-w-0">
                              <div className={`h-3 rounded-full ${isDarkMode ? "bg-gray-800" : "bg-gray-200/70"}`}>
                                <div
                                  className="h-full rounded-full bg-cyan-600"
                                  style={{ width: `${percent > 0 ? Math.max(2, Math.min(100, percent)) : 0}%` }}
                                />
                              </div>
                              <div className={`mt-1 flex items-center justify-between gap-3 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                                <span>
                                  {t("feedback.previous", { defaultValue: "Precedent" })}:{" "}
                                  {previousPercent === null ? "-" : formatPercent(previousPercent)}
                                </span>
                                <span className={Number.isFinite(entry.delta) && entry.delta !== 0 ? "font-semibold" : ""}>
                                  {deltaLabel}
                                </span>
                              </div>
                            </div>

                            <div className="text-right text-sm font-semibold tabular-nums">
                              {formatPercent(entry.percent)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </QuestionCard>
            );
          }

          if (insight.type === "time") {
            return (
              <QuestionCard
                key={insight.id}
                isDarkMode={isDarkMode}
                title={insight.title}
                badge="time"
                answered={insight.answered}
                total={insight.total}
              >
                <div className="mb-3 grid gap-3 sm:grid-cols-2">
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("feedback.average_hour", { defaultValue: "Heure moyenne" })}
                    value={
                      Number.isFinite(insight.averageHour)
                        ? `${Math.round(insight.averageHour)}h`
                        : "-"
                    }
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("feedback.average_hour", { defaultValue: "Heure moyenne" }),
                        rows: insight.detailRows,
                      })
                    }
                  />
                  <StatCard
                    isDarkMode={isDarkMode}
                    label={t("feedback.answers", { defaultValue: "Reponses" })}
                    value={String(insight.answered)}
                    onClick={() =>
                      openDetail({
                        title: insight.title,
                        subtitle: t("feedback.answers", { defaultValue: "Reponses" }),
                        rows: insight.detailRows,
                      })
                    }
                  />
                </div>

                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={insight.distribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                        <XAxis dataKey="label" tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <YAxis allowDecimals={false} tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} cursor={barCursor} />
                        <Bar
                          dataKey="count"
                          fill="#2563eb"
                          radius={[6, 6, 0, 0]}
                          activeBar={activeBar}
                          className="cursor-pointer"
                          onClick={(entry) => {
                            const bucket = entry?.payload;
                            openDetail({
                              title: `${insight.title} - ${bucket?.label ?? ""}`,
                              subtitle: t("feedback.answers", { defaultValue: "Reponses" }),
                              rows: bucket?.rows || [],
                            });
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={insight.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                        <XAxis dataKey="label" tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <YAxis domain={[0, 23]} tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} cursor={lineCursor} formatter={(value) => `${formatMetric(value, 0)}h`} />
                        <Line dataKey="averageHour" type="monotone" stroke="#1d4ed8" strokeWidth={2.5} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </QuestionCard>
            );
          }

          if (insight.type === "bodymap") {
            const hasTypeAverages = insight.bodymapTypeAverages.some(
              (entry) => Number.isFinite(entry.current) || Number.isFinite(entry.previous)
            );
            const getTypeDelta = (entry) =>
              Number.isFinite(entry.current) && Number.isFinite(entry.previous)
                ? entry.current - entry.previous
                : null;

            return (
              <QuestionCard
                key={insight.id}
                isDarkMode={isDarkMode}
                title={insight.title}
                badge="bodymap"
                answered={insight.answered}
                total={insight.total}
              >
                <div className="mb-3 grid gap-3 sm:grid-cols-3">
                  {insight.bodymapTypeAverages.map((entry) => {
                    const delta = getTypeDelta(entry);
                    return (
                      <StatCard
                        key={entry.key}
                        isDarkMode={isDarkMode}
                        label={entry.label}
                        value={formatMetric(entry.current)}
                        onClick={() =>
                          openDetail({
                            title: `${insight.title} - ${entry.label}`,
                            subtitle: t("feedback.answers", { defaultValue: "Reponses" }),
                            rows: entry.rows,
                          })
                        }
                        hint={
                          delta === null
                            ? t("feedback.no_comparison", { defaultValue: "Pas de comparaison disponible" })
                            : `${t("common.vs_prev", { defaultValue: "Vs veille" })}: ${delta > 0 ? "+" : ""}${formatMetric(delta)}`
                        }
                      />
                    );
                  })}
                </div>

                {insight.topZones.length === 0 && !hasTypeAverages ? (
                  <EmptyState
                    isDarkMode={isDarkMode}
                    text={t("feedback.no_data", { defaultValue: "Aucune donnee disponible" })}
                  />
                ) : (
                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="h-48">
                      {insight.topZones.length === 0 ? (
                        <EmptyState
                          isDarkMode={isDarkMode}
                          text={t("feedback.no_data", { defaultValue: "Aucune donnee disponible" })}
                        />
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={insight.topZones} layout="vertical" margin={{ left: 10, right: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                            <XAxis type="number" allowDecimals={false} tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                            <YAxis
                              type="category"
                              dataKey="label"
                              width={120}
                              tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                            />
                            <Tooltip contentStyle={tooltipStyle} cursor={barCursor} />
                            <Bar
                              dataKey="count"
                              fill="#0f766e"
                              radius={[0, 6, 6, 0]}
                              activeBar={activeBar}
                              className="cursor-pointer"
                              onClick={(entry) => {
                                const zone = entry?.payload;
                                openDetail({
                                  title: `${insight.title} - ${zone?.label ?? ""}`,
                                  subtitle: t("feedback.answers", { defaultValue: "Reponses" }),
                                  rows: zone?.rows || [],
                                });
                              }}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                    <div className="h-48">
                      {hasTypeAverages ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={insight.bodymapTypeAverages}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                            <XAxis dataKey="label" tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                            <YAxis domain={[0, 10]} tick={{ fill: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: 12 }} />
                            <Tooltip contentStyle={tooltipStyle} cursor={barCursor} formatter={(value) => formatMetric(value)} />
                            <Legend />
                            <Bar
                              name={t("feedback.current", { defaultValue: "Actuel" })}
                              dataKey="current"
                              fill="#0d9488"
                              radius={[6, 6, 0, 0]}
                              activeBar={activeBar}
                              className="cursor-pointer"
                              onClick={(entry) => {
                                const typeEntry = entry?.payload;
                                openDetail({
                                  title: `${insight.title} - ${typeEntry?.label ?? ""}`,
                                  subtitle: t("feedback.current", { defaultValue: "Actuel" }),
                                  rows: typeEntry?.rows || [],
                                });
                              }}
                            />
                            <Bar
                              name={t("feedback.previous", { defaultValue: "Precedent" })}
                              dataKey="previous"
                              fill="#94a3b8"
                              radius={[6, 6, 0, 0]}
                              activeBar={activeBar}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <EmptyState
                          isDarkMode={isDarkMode}
                          text={t("feedback.no_data", { defaultValue: "Aucune donnee disponible" })}
                        />
                      )}
                    </div>
                  </div>
                )}
              </QuestionCard>
            );
          }

          return (
            <TextQuestionOverviewCard
              key={insight.id}
              insight={insight}
              isDarkMode={isDarkMode}
              t={t}
              onOpenDetail={openDetail}
            />
          );
        })}
          </div>
        </>
      )}
      <FeedbackDetailModal
        isOpen={Boolean(detailModal)}
        onClose={() => setDetailModal(null)}
        isDarkMode={isDarkMode}
        title={detailModal?.title}
        subtitle={detailModal?.subtitle}
        rows={detailModal?.rows || []}
        columns={detailColumns}
        emptyLabel={t("feedback.no_data", { defaultValue: "Aucune donnée disponible" })}
        closeLabel={t("common.close", { defaultValue: "Fermer" })}
      />
    </div>
  );
};

export default SubjectiveFeedbackOverview;
